/**
 * Professional Quiz Data Index
 * Centralized export for efficient loading of quiz questions by user type
 */

// Topic categories for skill profiling radar chart
export const TOPIC_CATEGORIES = {
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    color: '#e74c3c',
    description: 'Marketing and promotional knowledge'
  },
  sales: {
    id: 'sales',
    label: 'Sales',
    color: '#9b59b6',
    description: 'Sales techniques and customer acquisition'
  },
  development: {
    id: 'development',
    label: 'Development',
    color: '#3498db',
    description: 'Technical development and engineering skills'
  },
  informationTechnology: {
    id: 'informationTechnology',
    label: 'IT',
    color: '#1abc9c',
    description: 'IT systems, automation, and digital tools'
  },
  administration: {
    id: 'administration',
    label: 'Administration',
    color: '#f39c12',
    description: 'Compliance, documentation, and procedures'
  },
  customerSupport: {
    id: 'customerSupport',
    label: 'Customer Support',
    color: '#2ecc71',
    description: 'Customer service and support skills'
  }
};

// Lazy load question modules for efficient bundle splitting
// Using JSON files that contain full metadata (Kategorie, Unterkategorie, etc.)
const questionModules = {
  installer: () => import('./ENInstallerQuestions.json'),
  energy_consultant: () => import('./ENEnergyConsultantQuestions.json'),
  salesperson: () => import('./ENSalespersonQuestions.json'),
  general_german: () => import('./DEQuestions.json'),
};

// User type profiles for quiz selection
export const USER_PROFILES = {
  installer: {
    id: "installer",
    title: "Installer",
    description: "HVAC professionals who install, commission, and maintain heat pump systems",
    icon: "🔧",
    color: "#1890ff",
    language: "EN",
    focusAreas: [
      "Installation procedures and best practices",
      "Refrigerant handling and charging",
      "Electrical connections and safety",
      "Commissioning and troubleshooting",
      "Maintenance and diagnostics"
    ]
  },
  energy_consultant: {
    id: "energy_consultant",
    title: "Energy Consultant / Designer",
    description: "Professionals who design heating systems, perform energy calculations, and advise on efficiency improvements",
    icon: "📊",
    color: "#1890ff",
    language: "EN",
    focusAreas: [
      "System design and sizing calculations",
      "Building physics and heat loss analysis",
      "Energy efficiency and SCOP optimization",
      "Cost-benefit analysis and ROI",
      "Regulatory compliance and standards"
    ]
  },
  salesperson: {
    id: "salesperson",
    title: "Salesperson / Customer Advisor",
    description: "Professionals who advise customers, present solutions, and guide purchasing decisions",
    icon: "💼",
    color: "#1890ff",
    language: "EN",
    focusAreas: [
      "Customer needs assessment and consultation",
      "Value proposition and benefits communication",
      "Objection handling and education",
      "Financing and incentive knowledge",
      "Relationship building and follow-up"
    ]
  },
  general_german: {
    id: "general_german",
    title: "Allgemein / Deutsch",
    description: "Umfassende Fragen zu Wärmepumpen auf Deutsch – ideal für alle Fachbereiche mit Fokus auf deutsche Normen und Vorschriften",
    icon: "🇩🇪",
    color: "#1890ff",
    language: "DE",
    focusAreas: [
      "Marketing und Wirtschaftlichkeit",
      "Umwelt und Regularien (GEG, VDI, DIN)",
      "Wärmepumpen-Technik und Grundlagen",
      "Systemauslegung und Integration",
      "Installation und Inbetriebnahme",
      "Elektrik und Wartung",
      "Kundensupport und Fehlerdiagnose"
    ]
  }
};

/**
 * Load questions for a specific user type
 * Uses dynamic import for efficient code splitting
 * @param {string} userType - 'installer', 'energy_consultant', 'salesperson', or 'general_german'
 * @returns {Promise<Array>} Array of question objects
 */
export const loadQuestions = async (userType) => {
  const loader = questionModules[userType];
  if (!loader) {
    throw new Error(`Unknown user type: ${userType}`);
  }
  
  const module = await loader();
  // JSON imports have the data as default export
  return module.default || module;
};

/**
 * Get random subset of questions
 * @param {Array} questions - Full question array
 * @param {number} count - Number of questions to select (default 5-10 random)
 * @returns {Array} Random selection of questions
 */
export const getRandomQuestions = (questions, count = null) => {
  // If count not specified, randomly select between 5-10 questions
  const numQuestions = count || Math.floor(Math.random() * 6) + 5; // 5-10
  
  // Shuffle array using Fisher-Yates algorithm
  const shuffled = [...questions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled.slice(0, Math.min(numQuestions, shuffled.length));
};

/**
 * Get questions focused on a specific topic
 * Always picks the questions with the highest topic weight,
 * adapting to whatever weight range exists in the data
 * @param {Array} questions - Full question array
 * @param {string} topicKey - Topic key (e.g., 'marketing', 'sales', 'development')
 * @param {number} count - Number of questions to select
 * @returns {Array} Questions prioritized by topic relevance
 */
export const getQuestionsByTopic = (questions, topicKey, count = 10) => {
  // Sort all questions by topic weight descending
  const sortedByTopic = [...questions].sort((a, b) => {
    const weightA = a.topicWeights?.[topicKey] || 0;
    const weightB = b.topicWeights?.[topicKey] || 0;
    return weightB - weightA;
  });
  
  // Determine adaptive threshold: use the median weight of the top N candidates
  // This ensures we always get relevant questions even if weights are low
  const topCandidates = sortedByTopic.slice(0, Math.min(count * 2, sortedByTopic.length));
  const topWeights = topCandidates.map(q => q.topicWeights?.[topicKey] || 0);
  const medianWeight = topWeights[Math.floor(topWeights.length / 2)] || 0;
  
  // Split into "best" (above median of top candidates) and "rest"
  const best = sortedByTopic.filter(q => (q.topicWeights?.[topicKey] || 0) > medianWeight);
  const rest = sortedByTopic.filter(q => (q.topicWeights?.[topicKey] || 0) <= medianWeight);
  
  // Shuffle each group
  const shuffleFn = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  const shuffledBest = shuffleFn(best);
  const shuffledRest = shuffleFn(rest);
  
  // Take as many high-weight questions as possible, fill rest from next best
  const result = [...shuffledBest.slice(0, count)];
  if (result.length < count) {
    result.push(...shuffledRest.slice(0, count - result.length));
  }
  
  // Final shuffle to mix
  return shuffleFn(result);
};

/**
 * Calculate quiz results and statistics including topic profiling
 * @param {Array} questions - Questions asked
 * @param {Array} answers - User's answers (indices)
 * @returns {Object} Results object with score, breakdown, topic scores, suggestions
 */
export const calculateResults = (questions, answers) => {
  let correct = 0;
  let incorrect = 0;
  const breakdown = {
    easy: { correct: 0, total: 0 },
    medium: { correct: 0, total: 0 },
    hard: { correct: 0, total: 0 }
  };
  
  // Initialize topic scores for radar chart
  const topicScores = {
    possible: {
      marketing: 0,
      sales: 0,
      development: 0,
      informationTechnology: 0,
      administration: 0,
      customerSupport: 0
    },
    achieved: {
      marketing: 0,
      sales: 0,
      development: 0,
      informationTechnology: 0,
      administration: 0,
      customerSupport: 0
    }
  };
  
  const details = questions.map((q, idx) => {
    const isCorrect = answers[idx] === q.correctIndex;
    if (isCorrect) {
      correct++;
    } else {
      incorrect++;
    }
    
    const difficulty = q.difficulty || 'medium';
    breakdown[difficulty].total++;
    if (isCorrect) {
      breakdown[difficulty].correct++;
    }
    
    // Calculate topic weights contribution
    if (q.topicWeights) {
      Object.keys(topicScores.possible).forEach(topic => {
        const weight = q.topicWeights[topic] || 0;
        topicScores.possible[topic] += weight;
        if (isCorrect) {
          topicScores.achieved[topic] += weight;
        }
      });
    }
    
    return {
      question: q.question,
      userAnswer: answers[idx],
      correctAnswer: q.correctIndex,
      isCorrect,
      explanation: q.explanation,
      difficulty
    };
  });
  
  const percentage = Math.round((correct / questions.length) * 100);
  
  // Normalize topic scores to percentages (0-100)
  // Both possible and achieved use the same scale (% of total questions)
  // This ensures achieved can never exceed possible
  const topicPercentages = {
    possible: {},
    achieved: {}
  };
  
  Object.keys(topicScores.possible).forEach(topic => {
    const maxPossible = topicScores.possible[topic];
    topicPercentages.possible[topic] = maxPossible > 0 
      ? Math.round((maxPossible / questions.length) * 100) 
      : 0;
    // Use same denominator as possible so achieved ≤ possible
    topicPercentages.achieved[topic] = Math.round((topicScores.achieved[topic] / questions.length) * 100);
  });
  
  // Generate performance level
  let performanceLevel;
  let performanceColor;
  if (percentage >= 90) {
    performanceLevel = 'Expert';
    performanceColor = '#52c41a';
  } else if (percentage >= 75) {
    performanceLevel = 'Proficient';
    performanceColor = '#1890ff';
  } else if (percentage >= 60) {
    performanceLevel = 'Developing';
    performanceColor = '#faad14';
  } else {
    performanceLevel = 'Needs Practice';
    performanceColor = '#ff4d4f';
  }
  
  // Generate suggestions based on performance
  const suggestions = [];
  if (breakdown.hard.total > 0 && breakdown.hard.correct / breakdown.hard.total < 0.5) {
    suggestions.push("Consider reviewing advanced technical concepts");
  }
  if (breakdown.medium.total > 0 && breakdown.medium.correct / breakdown.medium.total < 0.6) {
    suggestions.push("Focus on core knowledge areas through additional practice");
  }
  if (percentage >= 75) {
    suggestions.push("Great progress! Challenge yourself with more complex scenarios");
  }
  if (percentage < 60) {
    suggestions.push("Try the simulation tutorials to reinforce practical understanding");
  }
  if (percentage >= 90) {
    suggestions.push("Excellent mastery! Consider mentoring others in your field");
  }
  
  return {
    correct,
    incorrect,
    total: questions.length,
    percentage,
    performanceLevel,
    performanceColor,
    breakdown,
    details,
    suggestions,
    topicScores,
    topicPercentages
  };
};

// Quiz configuration constants
export const QUIZ_CONFIG = {
  MIN_QUESTIONS: 5,
  MAX_QUESTIONS: 10,
  DEFAULT_QUESTIONS: 7,
  PASSING_SCORE: 60,
  TIME_LIMIT_PER_QUESTION: null, // No time limit by default
};
